import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    TextField,
    InputAdornment,
    Card,
    CardContent,
    CardHeader,
    Grid,
    Chip,
    Stack,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Rating,
    Tooltip,
    Fab,
    Badge,
    Paper,
    Divider,
    IconButton,
    Avatar,
    LinearProgress
} from '@mui/material';
import {
    Search,
    FilterList,
    Build,
    Warning,
    CheckCircle,
    PlayArrow,
    ThumbUp,
    Comment,
    Visibility,
    ExpandMore,
    Error,
    Speed,
    ElectricalServices,
    LocalGasStation,
    Thermostat,
    Settings,
    QuestionAnswer,
    Timeline,
    Psychology,
    Phone,
    Close,
    Star,
    TrendingUp,
    BookmarkBorder,
    Bookmark
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api';

const CATEGORY_CONFIG = {
    engine_issues: { icon: Build, label: 'Engine Issues', color: '#f44336' },
    electrical_problems: { icon: ElectricalServices, label: 'Electrical Problems', color: '#ff9800' },
    cooling_system: { icon: Thermostat, label: 'Cooling System', color: '#2196f3' },
    fuel_system: { icon: LocalGasStation, label: 'Fuel System', color: '#4caf50' },
    generator_issues: { icon: Speed, label: 'Generator Issues', color: '#9c27b0' },
    maintenance_procedures: { icon: Settings, label: 'Maintenance', color: '#607d8b' },
    diagnostic_codes: { icon: Psychology, label: 'Diagnostic Codes', color: '#795548' },
    preventive_care: { icon: CheckCircle, label: 'Preventive Care', color: '#8bc34a' }
};

const DIFFICULTY_COLORS = {
    beginner: '#4caf50',
    intermediate: '#ff9800',
    advanced: '#f44336',
    expert: '#9c27b0'
};

const PRIORITY_COLORS = {
    low: '#4caf50',
    medium: '#ff9800',
    high: '#f44336',
    critical: '#d32f2f'
};

function APUTroubleshooting() {
    const navigate = useNavigate();
    const [guides, setGuides] = useState([]);
    const [popularGuides, setPopularGuides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedDifficulty, setSelectedDifficulty] = useState('all');
    const [selectedPriority, setSelectedPriority] = useState('all');
    const [sortBy, setSortBy] = useState('priority');
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [emergencyDialogOpen, setEmergencyDialogOpen] = useState(false);
    const [bookmarkedGuides, setBookmarkedGuides] = useState(new Set());

    // Fetch guides and filters
    const fetchGuides = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                search: searchTerm,
                category: selectedCategory,
                difficulty: selectedDifficulty,
                priority: selectedPriority,
                sortBy: sortBy,
                limit: 20
            });

            const [guidesResponse, popularResponse] = await Promise.all([
                apiClient.get(`/apu-troubleshooting?${params}`),
                apiClient.get('/apu-troubleshooting/popular?limit=3')
            ]);

            setGuides(guidesResponse.data.data);
            setPopularGuides(popularResponse.data.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching guides:', err);
            setError('Failed to load troubleshooting guides');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGuides();
    }, [searchTerm, selectedCategory, selectedDifficulty, selectedPriority, sortBy]);

    const handleGuideClick = (guideId) => {
        navigate(`/safety/apu-troubleshooting/${guideId}`);
    };

    const handleBookmark = (guideId) => {
        const newBookmarks = new Set(bookmarkedGuides);
        if (newBookmarks.has(guideId)) {
            newBookmarks.delete(guideId);
        } else {
            newBookmarks.add(guideId);
        }
        setBookmarkedGuides(newBookmarks);
        // In a real app, this would sync with backend
        localStorage.setItem('apuBookmarks', JSON.stringify([...newBookmarks]));
    };

    const getSeverityIcon = (priority) => {
        switch (priority) {
            case 'critical': return <Error sx={{ color: PRIORITY_COLORS[priority] }} />;
            case 'high': return <Warning sx={{ color: PRIORITY_COLORS[priority] }} />;
            case 'medium': return <Speed sx={{ color: PRIORITY_COLORS[priority] }} />;
            default: return <CheckCircle sx={{ color: PRIORITY_COLORS[priority] }} />;
        }
    };

    const QuickAccessCard = ({ guide }) => {
        const CategoryIcon = CATEGORY_CONFIG[guide.category]?.icon || Build;
        
        return (
            <Card 
                sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                    },
                    border: guide.priority === 'critical' ? '2px solid #f44336' : 'none'
                }}
                onClick={() => handleGuideClick(guide._id)}
            >
                <CardHeader
                    avatar={
                        <Avatar sx={{ bgcolor: CATEGORY_CONFIG[guide.category]?.color }}>
                            <CategoryIcon />
                        </Avatar>
                    }
                    action={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {getSeverityIcon(guide.priority)}
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleBookmark(guide._id);
                                }}
                            >
                                {bookmarkedGuides.has(guide._id) ? 
                                    <Bookmark color="primary" /> : 
                                    <BookmarkBorder />
                                }
                            </IconButton>
                        </Box>
                    }
                    title={
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                            {guide.title}
                        </Typography>
                    }
                    subheader={
                        <Typography variant="caption" color="text.secondary">
                            {CATEGORY_CONFIG[guide.category]?.label}
                        </Typography>
                    }
                    sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.85rem' }}>
                        {guide.description.length > 100 ? 
                            `${guide.description.substring(0, 100)}...` : 
                            guide.description
                        }
                    </Typography>
                    
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Chip 
                            label={guide.difficulty} 
                            size="small" 
                            sx={{ 
                                bgcolor: DIFFICULTY_COLORS[guide.difficulty],
                                color: 'white',
                                fontSize: '0.7rem'
                            }}
                        />
                        <Chip 
                            label={`${guide.troubleshootingSteps?.length || 0} steps`}
                            size="small" 
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                        />
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Tooltip title="View count">
                                <Stack direction="row" alignItems="center" spacing={0.3}>
                                    <Visibility sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="caption">{guide.metadata?.viewCount || 0}</Typography>
                                </Stack>
                            </Tooltip>
                            <Tooltip title="Average rating">
                                <Stack direction="row" alignItems="center" spacing={0.3}>
                                    <Star sx={{ fontSize: 16, color: '#ffc107' }} />
                                    <Typography variant="caption">
                                        {guide.metadata?.averageRating?.toFixed(1) || 'N/A'}
                                    </Typography>
                                </Stack>
                            </Tooltip>
                        </Stack>
                        
                        {guide.videoWalkthrough?.url && (
                            <Chip 
                                icon={<PlayArrow />} 
                                label="Video" 
                                size="small" 
                                color="primary"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                            />
                        )}
                    </Stack>
                </CardContent>
            </Card>
        );
    };

    if (loading && guides.length === 0) {
        return (
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <CircularProgress size={48} />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Loading APU troubleshooting guides...
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    🔧 APU Troubleshooting Center
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Quick access to step-by-step guides for APU issues. Find solutions fast when you're on the road.
                </Typography>
            </Box>

            {/* Emergency Contact Card */}
            <Alert 
                severity="error" 
                sx={{ mb: 3 }}
                action={
                    <Button 
                        color="inherit" 
                        size="small"
                        startIcon={<Phone />}
                        onClick={() => setEmergencyDialogOpen(true)}
                    >
                        Emergency
                    </Button>
                }
            >
                <strong>Safety First:</strong> If you smell fuel, see smoke, or encounter any dangerous situation, shut down the APU immediately and call for help.
            </Alert>

            {/* Search and Filter Bar */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            placeholder="Search troubleshooting guides..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                )
                            }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                label="Category"
                            >
                                <MenuItem value="all">All Categories</MenuItem>
                                {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                                    <MenuItem key={key} value={key}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <config.icon sx={{ fontSize: 18 }} />
                                            <span>{config.label}</span>
                                        </Stack>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<FilterList />}
                            onClick={() => setFilterDialogOpen(true)}
                            size="small"
                        >
                            Filters
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Popular Guides */}
            {popularGuides.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUp color="primary" />
                        Most Helpful Guides
                    </Typography>
                    <Grid container spacing={2}>
                        {popularGuides.map((guide) => (
                            <Grid item xs={12} md={4} key={guide._id}>
                                <QuickAccessCard guide={guide} />
                            </Grid>
                        ))}
                    </Grid>
                    <Divider sx={{ my: 3 }} />
                </Box>
            )}

            {/* All Guides */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                    All Troubleshooting Guides ({guides.length})
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {guides.length === 0 && !loading ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Build sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No troubleshooting guides found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Try adjusting your search terms or filters
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {guides.map((guide) => (
                        <Grid item xs={12} sm={6} lg={4} key={guide._id}>
                            <QuickAccessCard guide={guide} />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Filter Dialog */}
            <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Filter Guides</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel>Difficulty</InputLabel>
                            <Select
                                value={selectedDifficulty}
                                onChange={(e) => setSelectedDifficulty(e.target.value)}
                                label="Difficulty"
                            >
                                <MenuItem value="all">All Levels</MenuItem>
                                <MenuItem value="beginner">Beginner</MenuItem>
                                <MenuItem value="intermediate">Intermediate</MenuItem>
                                <MenuItem value="advanced">Advanced</MenuItem>
                                <MenuItem value="expert">Expert</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Priority</InputLabel>
                            <Select
                                value={selectedPriority}
                                onChange={(e) => setSelectedPriority(e.target.value)}
                                label="Priority"
                            >
                                <MenuItem value="all">All Priorities</MenuItem>
                                <MenuItem value="critical">Critical</MenuItem>
                                <MenuItem value="high">High</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="low">Low</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Sort By</InputLabel>
                            <Select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                label="Sort By"
                            >
                                <MenuItem value="priority">Priority</MenuItem>
                                <MenuItem value="rating">Rating</MenuItem>
                                <MenuItem value="views">Most Viewed</MenuItem>
                                <MenuItem value="newest">Newest</MenuItem>
                                <MenuItem value="updated">Recently Updated</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFilterDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Emergency Contact Dialog */}
            <Dialog open={emergencyDialogOpen} onClose={() => setEmergencyDialogOpen(false)}>
                <DialogTitle sx={{ color: 'error.main' }}>
                    🚨 Emergency Contacts
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Alert severity="error">
                            <strong>Immediate Danger:</strong> Call 911 first if there's fire, injury, or immediate threat to safety.
                        </Alert>
                        
                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Company Emergency Line
                            </Typography>
                            <Typography variant="h6" color="primary.main">
                                📞 1-800-555-HELP (4357)
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                24/7 roadside assistance and emergency support
                            </Typography>
                        </Paper>

                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Maintenance Hotline
                            </Typography>
                            <Typography variant="h6" color="primary.main">
                                📞 1-800-555-MAINT (6248)
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Technical support for APU and equipment issues
                            </Typography>
                        </Paper>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmergencyDialogOpen(false)} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {loading && guides.length > 0 && (
                <LinearProgress sx={{ mt: 2 }} />
            )}
        </Container>
    );
}

export default APUTroubleshooting; 